import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Permission, resolvePreset } from '@construction/shared';
import { hiddenWorksApi, sitesApi } from '@/api/endpoints';
import { Alert, Button, Drawer, EmptyState, FilterBar, PageHeader, Segmented, Spinner } from '@/components/ui';
import { DownloadButton } from '@/features/exports/DownloadButton';
import { FilterChip } from '@/features/filters/FilterChip';
import { RangeField } from '@/features/filters/RangeField';
import { HiddenWorkForm } from '@/features/hidden-works/HiddenWorkForm';
import {
  HiddenWorkDetail,
  HiddenWorkGrid,
  HiddenWorkList,
  HiddenWorkTable,
} from '@/features/hidden-works/HiddenWorkViews';
import { MyHiddenWorksList } from '@/features/hidden-works/MyHiddenWorksList';
import { RecordTabs } from '@/features/records/RecordTabs';
import { QueueBar } from '@/features/upload-queue';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { useMobileShell } from '@/hooks/useMobileShell';
import { useMobileSite } from '@/hooks/useCurrentSite';
import { useRecordTab } from '@/hooks/useRecordTab';
import { usePermissions } from '@/lib/permissions';
import { siteDateKey, siteDayRange } from '@/lib/site-time';
import { useAuthStore } from '@/store/auth';

/** 服务端 `take: 200`。取满了要说出来，不能让更早的记录静默消失 */
const LIST_CAP = 200;

/**
 * 隐蔽工程页（CR-042：一页两个 tab，两套外壳共用）。
 *
 * 客户原话：「拍隐蔽工程 这个tab完全可以整合进 隐蔽工程 tab，根据权限显示按键 拍隐蔽工程，
 * 里面的记录也可以根据权限显示记录tabs分类，我的/本工地」。
 * - 有 `hiddenWork.write` → 页头「拍隐蔽工程」+「我的」tab（我在这个工地拍过的）。
 *   App 里按钮展开拍摄表单；电脑上弹出「请用 App 拍」—— 照片必须当场用相机拍（约束 5），
 *   表单在网页端永不渲染；
 * - 有 `hiddenWork.view` → 「本工地」tab；
 * - 两个都有才显示「本工地 / 我的」，**默认「本工地」**（CR-048 推翻 CR-042 的「默认我的」）。
 *
 * **筛选两个 tab 共用**（CR-048，客户原话「我的tab里复用 本工地tab的筛选」）：
 * 区间和类别切 tab 不变；拍摄人和下载只在「本工地」出现（「我的」永远是自己）。
 *
 * 「本工地」为什么默认是表不是图片墙（CR-031）：管理端真正要问的是「三楼防水是哪天做的」
 * 「上个月老陈拍的那批在哪」—— 那是**查找**问题。CR-082 加了「照片」看法（封面网格），点一条都是右侧 / 底部的详情。
 *
 * CR-082：下载（次按钮）和拍隐蔽工程（主按钮）在页头；工具条是 本工地 / 我的 + 日期 / 类别 / 拍摄人 三个胶囊 + 右边汇总。
 */
export function HiddenWorksPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const { can } = usePermissions();
  const canWrite = can(Permission.HIDDEN_WORK_WRITE);
  const canView = can(Permission.HIDDEN_WORK_VIEW);
  const canExport = can(Permission.HIDDEN_WORK_EXPORT);
  const isMobileShell = useMobileShell();

  const { site, isLoading } = useMobileSite();
  const { tab, showTabs, setTab } = useRecordTab({ canMine: canWrite, canSite: canView });

  // `?compose=1`：拍照途中被安卓回收之后，恢复层把人送回这个地址，表单要自己展开（CR-037）
  const [params] = useSearchParams();
  const [composing, setComposing] = useState(() => params.get('compose') === '1');
  const [noticeOpen, setNoticeOpen] = useState(false);

  // 两个 tab 共用的筛选（CR-048）。区间按工地时区算「今天」
  const tz = site?.timezone ?? 'America/Los_Angeles';
  const today = siteDateKey(tz);
  const [range, setRange] = useState(() => resolvePreset('thisWeek', today));
  const [category, setCategory] = useState('');
  const [userId, setUserId] = useState('');

  const siteId = site?.id ?? null;
  const categories = useQuery({
    queryKey: ['hidden-categories', siteId],
    queryFn: () => hiddenWorksApi.categories(siteId!),
    enabled: siteId !== null,
  });

  // 拍摄人只要 id 和姓名（CR-040）。只在「本工地」出现，所以只在那边查 ——
  // 只能拍、不能看全工地的人没有 member-options 的权限
  const members = useQuery({
    queryKey: ['member-options', siteId],
    queryFn: () => sitesApi.memberOptions(siteId!),
    enabled: siteId !== null && tab === 'site',
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20 text-ink-3">
        <Spinner />
      </div>
    );
  }
  if (!site) {
    return <EmptyState title={t('site.empty')} hint={t('site.emptyHintWorker')} />;
  }

  const toolbar = (
    <>
      {showTabs && <RecordTabs tab={tab} onChange={setTab} />}
      <RangeField
        from={range.from}
        to={range.to}
        today={today}
        presets={['today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth']}
        onChange={setRange}
      />
      <FilterChip<string>
        label={t('hidden.category')}
        value={category || null}
        onChange={(next) => setCategory(next ?? '')}
        options={[
          { value: null, label: t('hidden.allCategories') },
          ...(categories.data ?? []).map((c) => ({ value: c, label: c })),
        ]}
      />
      {/* 拍摄人只在「本工地」有意义：「我的」永远是自己 */}
      {tab === 'site' && (
        <FilterChip<string>
          label={t('hidden.byUser')}
          value={userId || null}
          onChange={(next) => setUserId(next ?? '')}
          options={[
            { value: null, label: t('hidden.allUsers') },
            ...(members.data ?? []).map((m) => ({ value: String(m.id), label: m.name })),
          ]}
        />
      )}
    </>
  );

  return (
    <>
      <PageHeader
        title={t('nav.hidden')}
        description={site.name}
        action={
          <>
            {/* 下载的就是眼前这份：同一个区间、类别、拍摄人（CR-043）。「我的」没有下载，下的是全工地的 */}
            {tab === 'site' && canExport && (
              <DownloadButton
                path={`/sites/${site.id}/exports/hidden-works`}
                query={{
                  from: range.from,
                  to: range.to,
                  category: category || undefined,
                  userId: userId || undefined,
                }}
                label={t('export.download')}
                action="hiddenWork.export"
              />
            )}
            {canWrite && (
              // 点了打开表单、文字不变；电脑上弹出「请用 App 拍」
              <Button
                onClick={() => (isMobileShell ? setComposing(true) : setNoticeOpen(true))}
                data-action="hidden.capture"
              >
                {t('hidden.capture')}
              </Button>
            )}
          </>
        }
      />

      {canWrite && (
        <div className="mb-4">
          <QueueBar />
        </div>
      )}

      {/* 只在 App 样式里渲染表单：电脑浏览器保证不了「当场用相机拍」 */}
      {isMobileShell && composing && user && (
        <HiddenWorkForm
          site={site}
          userId={user.id}
          // 提交之后切到「我的」：那条「上传中」在那边（CR-048 默认落在「本工地」之后）
          onDone={() => {
            setComposing(false);
            if (showTabs) setTab('mine');
          }}
          onCancel={() => setComposing(false)}
        />
      )}

      {tab === 'mine' ? (
        <MyHiddenWorksList site={site} from={range.from} to={range.to} category={category} toolbar={toolbar} />
      ) : (
        <SiteHiddenWorks
          site={site}
          from={range.from}
          to={range.to}
          category={category}
          userId={userId}
          toolbar={toolbar}
        />
      )}

      <Drawer open={noticeOpen} title={t('hidden.capture')} onClose={() => setNoticeOpen(false)}>
        <p className="text-body font-semibold text-ink">{t('notice.hiddenCaptureTitle')}</p>
        <p className="mt-2 text-body leading-relaxed text-ink-2">{t('notice.hiddenCaptureBody')}</p>
      </Drawer>
    </>
  );
}

/**
 * 「本工地」tab（列表页模板，DESIGN-SYSTEM 第 5 节）：工具条 + 表格 / 照片 / 列表 + 详情。
 * 汇总写在工具条右边：服务端 `take: 200`，取满了要说出来。
 */
function SiteHiddenWorks({
  site,
  from,
  to,
  category,
  userId,
  toolbar,
}: {
  site: { id: number; timezone: string };
  from: string;
  to: string;
  category: string;
  /** 胶囊的值，字符串；空串是「全部人员」 */
  userId: string;
  toolbar: ReactNode;
}) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const tz = site.timezone;
  const isMobileShell = useMobileShell();
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [openId, setOpenId] = useState<string | null>(null);

  const records = useQuery({
    queryKey: ['hidden-works', site.id, category, userId, from, to],
    queryFn: () =>
      hiddenWorksApi.list(site.id, {
        category: category || undefined,
        // 胶囊的值天生是字符串，接口要整数（CLAUDE.md 约束 11）
        userId: /^\d+$/.test(userId) ? Number(userId) : undefined,
        // 同考勤页：区间要按工地时区换算，不能在日期后面直接拼 Z
        ...siteDayRange(from, to, tz),
      }),
  });

  const list = records.data ?? [];
  const photoCount = list.reduce((sum, r) => sum + r.photos.length, 0);
  const opened = list.find((r) => r.id === openId) ?? null;

  const summary = list.length > 0 && (
    <span className="flex flex-wrap gap-x-4">
      <span>{t('hidden.totalRecords', { count: list.length })}</span>
      <span>{t('hidden.totalPhotos', { count: photoCount })}</span>
      {list.length >= LIST_CAP && <span className="text-safety-600">{t('hidden.cappedHint', { count: LIST_CAP })}</span>}
    </span>
  );

  return (
    <>
      <FilterBar summary={summary}>
        {toolbar}
        {!isMobileShell && (
          <Segmented<'list' | 'grid'>
            label={t('hidden.view')}
            hideLabel
            value={view}
            onChange={setView}
            options={[
              { value: 'list', label: t('hidden.viewList') },
              { value: 'grid', label: t('hidden.viewPhotos') },
            ]}
          />
        )}
      </FilterBar>

      {records.isLoading && (
        <div className="flex justify-center py-16 text-ink-3">
          <Spinner />
        </div>
      )}
      {records.isError && <Alert>{toMessage(records.error)}</Alert>}
      {!records.isLoading && list.length === 0 && <EmptyState title={t('hidden.empty')} />}

      {list.length > 0 &&
        (view === 'grid' && !isMobileShell ? (
          <HiddenWorkGrid records={list} timezone={tz} onOpen={setOpenId} />
        ) : (
          <>
            {/* 窄屏：列表（CR-036）；电脑：表格 */}
            <HiddenWorkList records={list} timezone={tz} onOpen={setOpenId} className="xl:hidden" />
            <HiddenWorkTable records={list} timezone={tz} openId={openId} onOpen={setOpenId} />
          </>
        ))}

      <HiddenWorkDetail record={opened} timezone={tz} onClose={() => setOpenId(null)} />
    </>
  );
}
